import 'tuple-it/register';
import './global.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';

import { Toaster } from 'react-hot-toast';
import GlobalStyle from './_layouts/globalStyles';
import { FallBack } from './components/fallBack';
import { ThemeProvider, themeConfig } from './theme/v2';

import { IS_BINARY } from './constants';
import { fsRoutes } from './routes/fs-routes';

import * as Sentry from '@sentry/react';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { useEffect } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  createHashRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

import pino from 'pino';
import 'react-circular-progressbar/dist/styles.css';
import { NotFound } from './components/notFound';
import './i18n';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { browserWindowAPI } from './browser';
import { IdbGuard } from './components/idb-guard';
import { InitialLoading } from './components/loading/initial';
import { Snackbar } from './components/snackbar';
import { queryClient } from './config';
import { AppConfig } from './contexts/app-config';
import { BinaryConfigProvider } from './contexts/binaryConfig';
import { BroadcastPersonal } from './contexts/broadcast-personal';
import { ReportJobsProvider } from './contexts/report-jobs';
import { SerialPortProvider } from './contexts/serial-port';
import { defaultSettings } from './contexts/settings/settings-config';
import { SettingsProvider } from './contexts/settings/settings-provider';

declare global {
  var logger: pino.Logger;
}

export function render() {
  Sentry.init({
    dsn: 'https://097e4aa1bd164b1c88f96fcef5d16c1f@sentry.kascosys.com.br/9',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration()
    ],

    tracesSampleRate: 1.0,

    tracePropagationTargets: ['localhost', /^https:\/\/kascosys\.com.br/, /^fdm:\/\//],

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0
  });

  // HashRouter no Electron: produção carrega via file://, sem servidor pra reescrever URL.
  const createRouter = Sentry.wrapCreateBrowserRouter(IS_BINARY ? createHashRouter : createBrowserRouter);
  const router = createRouter([...fsRoutes(), { path: '*', element: <NotFound /> }]);

  const container = document.getElementById('root');
  const root = createRoot(container!);

  return root.render(
    <QueryClientProvider client={queryClient}>
      <SettingsProvider defaultSettings={defaultSettings}>
        <ThemeProvider modeStorageKey={themeConfig.modeStorageKey} defaultMode={themeConfig.defaultMode}>
          <Sentry.ErrorBoundary
            fallback={<FallBack />}
            onError={(error) => {
              logger.error(error, 'ErrorBoundary');
            }}
          >
            <NuqsAdapter>
              <GlobalStyle />

              <IdbGuard />

              <AppConfig>
                <BroadcastPersonal>
                  <BinaryConfigProvider>
                    <SerialPortProvider>
                      <ReportJobsProvider>
                        <RouterProvider
                          router={router}
                          fallbackElement={<InitialLoading text="Carregando o feed manager..." />}
                        />
                      </ReportJobsProvider>
                    </SerialPortProvider>
                  </BinaryConfigProvider>
                </BroadcastPersonal>
              </AppConfig>

              <Snackbar position="bottom-right" />

              <Toaster
                position="top-center"
                reverseOrder={false}
                toastOptions={{
                  duration: 5000,
                  style: {
                    background: '#333',
                    color: '#fff',
                    textAlign: 'left'
                  }
                }}
              />
            </NuqsAdapter>
          </Sentry.ErrorBoundary>
        </ThemeProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
}

if (!IS_BINARY) {
  window.electronAPI = browserWindowAPI;

  window.logger = pino({
    browser: {
      serialize: true,
      write(obj) {
        console.log(JSON.stringify(obj));
      }
    }
  }).child({ name: 'frontend' });

  render();
}
